import { Formik, Form } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import FormikFieldArray from "../components/common/FormikFieldArray";
import { useMemo } from "react";
import { useFetchAllModifierOptionsQuery } from "../store";
import {
  FaLayerGroup,
  FaListUl,
  FaSortNumericDown,
  FaPlusCircle,
} from "react-icons/fa";

interface ModifierGroupFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
  restaurantId: string;
}

const ModifierGroupForm: React.FC<ModifierGroupFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
  restaurantId,
}) => {
  const { data: modifierOptions } =
    useFetchAllModifierOptionsQuery(restaurantId);

  // Map modifierOptions for dropdown usage
  const availableOptions = useMemo(() => {
    if (!modifierOptions) return [];
    return modifierOptions.map((opt: any) => ({
      value: opt._id,
      label: opt.displayName,
    }));
  }, [modifierOptions]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => (
        <Form className="max-w-4xl mx-auto w-full pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - General Info */}
            <div className="space-y-6">
              {/* Basic Info Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaLayerGroup className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Informations Générales
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="displayName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Nom du groupe
                    </label>
                    <TextField
                      id="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Choix de la sauce"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="displayOrder"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ordre d&apos;affichage
                    </label>
                    <TextField
                      id="displayOrder"
                      value={values.displayOrder}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: 1"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Quantity Limits Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaSortNumericDown className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Règles de Quantité
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="quantityInfo.minPermitted"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Min. autorisé
                    </label>
                    <TextField
                      id="quantityInfo.minPermitted"
                      value={values.quantityInfo.minPermitted}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="0"
                      className="w-full text-center"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="quantityInfo.maxPermitted"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Max. autorisé
                    </label>
                    <TextField
                      id="quantityInfo.maxPermitted"
                      value={values.quantityInfo.maxPermitted}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      placeholder="1"
                      className="w-full text-center"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Définit combien d&apos;options le client doit/peut choisir
                  dans ce groupe.
                </p>
              </div>
            </div>

            {/* Right Column - Options */}
            <div className="space-y-6">
              {/* Options Selection Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-full">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <FaListUl className="text-primary" />
                  <h3 className="font-bold text-gray-800 text-lg">
                    Options du groupe
                  </h3>
                </div>

                <FormikFieldArray
                  name="modifierOptions"
                  pushItem={null}
                  renderItem={(opt: any) => (
                    <span className="font-medium text-gray-700">
                      {opt.displayName}
                    </span>
                  )}
                  renderAddButton={(push) => (
                    <div className="relative mt-2">
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none cursor-pointer"
                        onChange={(e) => {
                          const selected = availableOptions.find(
                            (o: any) => o.value === e.target.value
                          );
                          if (selected) {
                            push({
                              _id: selected.value,
                              displayName: selected.label,
                            });
                          }
                          e.target.value = "";
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          + Ajouter une option
                        </option>
                        {availableOptions
                          .filter(
                            (opt: any) =>
                              !values.modifierOptions.some(
                                (sel: any) => sel._id === opt.value
                              )
                          )
                          .map((opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                        <FaPlusCircle />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
            <Button
              className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-bold shadow-md transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={isSubmitting || isLoading}
              type="submit"
            >
              {isLoading ? "Enregistrement..." : "Enregistrer le groupe"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ModifierGroupForm;
